import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Check, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = [['month', 'a month'], ['year', 'a year'], ['one-off', 'one-off']];
const fmtPeriod = (p) => (PERIODS.find(([k]) => k === p) || [p, p.startsWith('one') ? p : `per ${p}`])[1];
const SOURCE = { card: 'from card payment', invoice: 'from invoice', manual: 'entered by hand' };

export const ContractEditor = ({ companyId, contract, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(contract?.amount ?? '');
  const [period, setPeriod] = useState(contract?.period || 'year');
  const [saving, setSaving] = useState(false);

  if (!companyId) return <span className="text-[11px] text-slate-600 shrink-0">No company linked</span>;

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/lists/contract/${companyId}`, { amount: amount === '' ? null : Number(amount), period });
      onSaved(res.data.contract);
      setEditing(false);
      toast.success('Contract amount saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save contract amount'); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()} data-testid={`contract-edit-${companyId}`}>
        <span className="text-xs text-slate-400">£</span>
        <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus
          className="w-20 bg-slate-950 border border-slate-700 rounded px-1.5 py-1 text-xs text-white" data-testid="contract-amount-input" />
        <select value={period} onChange={(e) => setPeriod(e.target.value)}
          className="bg-slate-950 border border-slate-700 rounded px-1 py-1 text-xs text-white" data-testid="contract-period-select">
          {PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button onClick={save} disabled={saving} className="p-1 text-emerald-400 hover:text-emerald-300" data-testid="contract-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => setEditing(false)} className="p-1 text-slate-500 hover:text-slate-300" data-testid="contract-cancel-btn"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="group flex flex-col items-end shrink-0 text-right" title={contract ? SOURCE[contract.source] : 'Add contract amount'}
      data-testid={`contract-value-${companyId}`}>
      <span className={`text-sm font-semibold flex items-center gap-1 ${contract ? 'text-white' : 'text-slate-500'}`}>
        {contract ? `£${Number(contract.amount).toLocaleString('en-GB', { maximumFractionDigits: 2 })}` : 'Add amount'}
        <Pencil className="w-3 h-3 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
      </span>
      {contract && <span className="text-[10px] text-slate-500">{fmtPeriod(contract.period)} · {SOURCE[contract.source] || ''}</span>}
    </button>
  );
};
